import React, { useEffect, useRef } from 'react'
import { X, FileText, Edit, Plus, PanelLeftClose, PanelLeft } from 'lucide-react'
import { useTabs } from '../../contexts/TabsContext'

/**
 * TabBar Component
 * 
 * Displays open tabs with close buttons and tab switching functionality
 */
const TabBar = ({ onToggleNotesList, isNotesListVisible }) => {
  const { tabs, activeTabId, switchToTab, closeTab, openTab } = useTabs()
  const scrollContainerRef = useRef(null)

  /**
   * Handle creating a new note
   */
  const handleCreateNote = () => {
    // Generate a unique ID for each new note tab
    const uniqueId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Count existing new note tabs to create unique title
    const newNoteCount = tabs.filter(tab => tab.type === 'new').length + 1
    const uniqueTitle = `New Note ${newNoteCount}`
    
    openTab({
      type: 'new',
      noteId: uniqueId,
      title: uniqueTitle,
      note: null
    })
  }

  /**
   * Scroll active tab into view when activeTabId changes
   */
  useEffect(() => {
    if (activeTabId && scrollContainerRef.current) {
      const activeTabElement = scrollContainerRef.current.querySelector(`[data-tab-id="${activeTabId}"]`)
      if (activeTabElement) {
        activeTabElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        })
      }
    }
  }, [activeTabId])

  /**
   * Get tab icon based on type
   */
  const getTabIcon = (type) => {
    switch (type) {
      case 'view':
        return <FileText className="h-4 w-4" />
      case 'edit':
        return <Edit className="h-4 w-4" />
      case 'new':
        return <Plus className="h-4 w-4" />
      default:
        return <FileText className="h-4 w-4" />
    }
  }

  /**
   * Handle tab close with confirmation if dirty
   */
  const handleCloseTab = (e, tab) => {
    e.stopPropagation()
    
    if (tab.isDirty) {
      const title = tab.title && typeof tab.title === 'string' ? tab.title : 'Untitled'
      const confirmed = window.confirm(
        `You have unsaved changes in "${title}". Are you sure you want to close this tab?`
      )
      if (!confirmed) return
    }
    
    closeTab(tab.id)
  }

  /**
   * Handle context menu for tab options
   */
  const handleContextMenu = (e, tab) => {
    e.preventDefault()
    // Could implement a context menu here in the future
  }

  /**
   * Truncate long tab titles
   */
  const truncateTitle = (title, maxLength = 20) => {
    // Ensure title is a string
    if (!title || typeof title !== 'string') {
      return 'Untitled'
    }
    
    if (title.length <= maxLength) return title
    return title.substring(0, maxLength) + '...'
  }

  return (
    <div className="absolute top-0 left-0 right-0 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-700/50 w-full max-w-full overflow-hidden tabbar-container z-40 hidden md:block" style={{ height: '61px' }}>
      <div className="flex items-center min-w-0 max-w-full px-4 h-full">
        {/* Notes List Toggle Button */}
        <div className="flex-shrink-0 mr-6">
          <button
            onClick={onToggleNotesList}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)' }}
            title={isNotesListVisible ? 'Hide notes list' : 'Show notes list'}
          >
            {isNotesListVisible ? (
              <PanelLeftClose className="h-4 w-4" />
            ) : (
              <PanelLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Tabs Container */}
        <div className="flex-1 min-w-0 overflow-hidden tab-container-wrapper">
          <div ref={scrollContainerRef} className="flex items-center space-x-1 tab-scroll-container">
            {tabs.map((tab) => (
              <div
                key={tab.id}
                data-tab-id={tab.id}
                className={`
                  group flex items-center space-x-2 px-3 text-sm font-medium cursor-pointer
                  border-b-2 transition-colors duration-200 flex-shrink-0 min-w-0 max-w-48
                  ${tab.note?.isUnsaved || tab.note?.isTemporary ? 'unsaved' : ''}
                  ${activeTabId === tab.id
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }
                `}
                 style={{ height: '56px' }}
                onClick={() => switchToTab(tab.id)}
                onContextMenu={(e) => handleContextMenu(e, tab)}
                title={tab.title && typeof tab.title === 'string' ? tab.title : 'Untitled'}
              >
                {/* Tab Icon */}
                <span className="flex-shrink-0">
                  {getTabIcon(tab.type)}
                </span>
                
                {/* Tab Title */}
                <span className="truncate min-w-0 flex-1">
                  {truncateTitle(tab.title, 15)}
                  {(tab.isDirty || tab.note?.isUnsaved || tab.note?.isTemporary) && (
                    <span className="unsaved-indicator ml-1">•</span>
                  )}
                </span>
                
                {/* Close Button */}
                <button
                  onClick={(e) => handleCloseTab(e, tab)}
                  className={`
                    flex-shrink-0 p-1 rounded-full transition-colors duration-200
                    ${activeTabId === tab.id
                      ? 'text-indigo-500 hover:text-indigo-700 hover:bg-indigo-100 dark:hover:bg-indigo-800'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                    opacity-0 group-hover:opacity-100
                  `}
                  title="Close tab"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Tab Actions */}
        <div className="flex items-center space-x-4 ml-6 flex-shrink-0">
          {/* New Note Button */}
          <button
            onClick={handleCreateNote}
            className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)' }}
            title="Create new note"
          >
            <Plus className="h-4 w-4" />
          </button>

        </div>
      </div>
    </div>
  )
}

export default TabBar 